/**
 * Custom hook for Solar Design save/load/delete operations
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useSolarSave({
  roofPolygons, obstaclePolygons, obstacleHeights, roofSpecs, spec,
  designName, clientName, moduleBrand, linkedProjectId, leadIdFromRoute,
  layout, currentDesignId, setCurrentDesignId,
  setHasUnsaved, projectIdFromRoute, removedPanels,
  searchQuery, getMapView, specificYield, dcAcRatio,
}) {
  const [saving, setSaving] = useState(false);
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  // Use ref to avoid stale closures with many deps
  const stateRef = useRef({});
  useEffect(() => {
    stateRef.current = {
      roofPolygons, obstaclePolygons, obstacleHeights, roofSpecs, spec,
      designName, clientName, moduleBrand, linkedProjectId, leadIdFromRoute,
      layout, currentDesignId, projectIdFromRoute, removedPanels,
      searchQuery, getMapView, specificYield, dcAcRatio,
    };
  });

  const fetchSavedDesigns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/solar-designs`);
      setSavedDesigns(data);
    } catch (err) { console.error(err); }
  }, []);

  const saveDesign = useCallback(async (silent = false) => {
    const s = stateRef.current;
    if (!s.roofPolygons.length) { toast.error("Draw at least one roof polygon first"); return; }
    setSaving(true);
    const view = (typeof s.getMapView === "function") ? s.getMapView() : null;
    const payload = {
      name: s.designName || "Untitled Design",
      client_name: s.clientName,
      module_brand: s.moduleBrand,
      location_search: s.searchQuery || "",
      map_center: view ? view.center : null,
      map_zoom: view ? view.zoom : null,
      roof_polygons: s.roofPolygons,
      obstacle_polygons: s.obstaclePolygons,
      obstacle_heights: s.obstacleHeights,
      roof_specs: s.roofSpecs,
      active_spec: s.spec,
      removed_panels: s.removedPanels || [],
      specific_yield: s.specificYield,
      dc_ac_ratio: s.dcAcRatio,
      layout: s.layout,
      project_id: s.linkedProjectId || s.projectIdFromRoute || null,
      lead_id: s.leadIdFromRoute || null,
    };
    try {
      if (s.currentDesignId) {
        await axios.put(`${API}/solar-designs/${s.currentDesignId}`, payload);
        if (!silent) toast.success("Design updated");
      } else {
        const { data } = await axios.post(`${API}/solar-designs`, payload);
        setCurrentDesignId(data.id);
        if (!silent) toast.success("Design saved");
      }
      setHasUnsaved(false);
      fetchSavedDesigns();
    } catch (err) { toast.error("Save failed: " + (err.response?.data?.detail || err.message)); }
    setSaving(false);
  }, [setCurrentDesignId, setHasUnsaved, fetchSavedDesigns]);

  const deleteDesign = useCallback(async (id) => {
    if (!window.confirm("Delete this design?")) return;
    try {
      await axios.delete(`${API}/solar-designs/${id}`);
      toast.success("Design deleted");
      if (stateRef.current.currentDesignId === id) { setCurrentDesignId(null); setHasUnsaved(false); }
      fetchSavedDesigns();
    } catch (err) { toast.error("Delete failed"); }
  }, [setCurrentDesignId, setHasUnsaved, fetchSavedDesigns]);

  return {
    saving, savedDesigns, showSaved, setShowSaved,
    fetchSavedDesigns, saveDesign, deleteDesign,
  };
}
